import {
  formatDateTime,
  DATE_FORMATS,
  ENVIRONMENTS,
  useIntroVideo,
} from '@belong/common';
import { ButtonBase, IntroVideo, Text } from '@belong/ui';
import MultipleBelongEmployeeProfileView from 'components/MultipleBelongEmployeeProfileView/MultipleBelongEmployeeProfileView';
import { getString } from 'strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import { formatFirstLine } from 'utils/formatAddress';
import PostInspectionContentLayout from '../../../PostInspectionContentLayout/PostInspectionContentLayout';
import { getPricingAssignedEmployee } from '../utils';

const VIDEO_URL =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/pricing-flow/pricing_report_video_final.mp4';
const ADDRESS_ICON_SRC =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/pricing-flow/ic_pricingreport_address.svg';

type Props = {
  onSaveAndNext: () => void;
  stepConfig: any;
  stepConfigs: any;
  stepResource: any;
};

const GetStarted = ({
  stepResource,
  stepConfig,
  stepConfigs,
  onSaveAndNext,
}: Props) => {
  const { report } = stepResource;
  const isStepCompleted = stepConfig?.status === 'Completed';
  // Bypass the "must watch video" gate on all non-production environments
  // (local, staging, dev1/2/3, etc.) so engineers can test quickly.
  const shouldBypassVideoGate =
    process.env.NEXT_PUBLIC_ENV !== ENVIRONMENTS.PRODUCTION;

  const introVideo = useIntroVideo({
    storageKey: `pricing:${report?.id ?? report?.subType ?? 'default'}`,
    initiallyUnlocked: isStepCompleted || shouldBypassVideoGate,
  });
  const { isUnlocked: videoEnded, progress, remainingTime } = introVideo;

  const isFirstPricingReport = report?.subType === 'PricingPostInspection';
  const assignedEmployee = getPricingAssignedEmployee(stepResource);

  const titleText = getString(
    isFirstPricingReport
      ? POST_INSPECTION_FLOW_STRINGS['pricing.getStarted.title']
      : POST_INSPECTION_FLOW_STRINGS['pricing.getStarted.title_second'],
    { firstName: stepResource.homeListingDetailsModel?.owner?.firstName },
  );

  return (
    <PostInspectionContentLayout
      showCityBackground={false}
      noOverlay
      stepConfig={stepConfig}
      stepConfigs={stepConfigs}
      title={titleText}
      employee={assignedEmployee}
    >
      {/* Address Header */}
      {stepResource.homeListingDetailsModel?.address && (
        <div className="mb-lg mt-2xl flex items-center justify-start gap-xs md:justify-center">
          <img
            src={ADDRESS_ICON_SRC}
            alt=""
            className="h-[30px] w-auto shrink-0"
          />
          <Text
            fontWeight="semibold"
            variant="body"
            className="text-left md:text-center"
          >
            {formatFirstLine(
              stepResource.homeListingDetailsModel.address,
              stepResource.homeListingDetailsModel?.basicInfo?.unitNumber,
            )}{' '}
            Pricing Plan
          </Text>
        </div>
      )}

      {/* Video */}
      <IntroVideo
        video={introVideo}
        videoSrc={VIDEO_URL}
        className="mb-xl bg-white"
      />

      {/* Desktop CTA with progress */}
      <div className="mb-3xl hidden flex-col items-center gap-xs md:flex">
        {videoEnded ? (
          <ButtonBase
            className="flex h-[59px] w-button-large items-center justify-center rounded bg-green transition duration-hover hover:bg-light-green focus:bg-light-green active:bg-light-green"
            onClick={() => onSaveAndNext()}
          >
            <Text fontWeight="semibold" className="text-white">
              {POST_INSPECTION_FLOW_STRINGS['pricing.getStarted.cta']}
            </Text>
          </ButtonBase>
        ) : (
          <>
            <div className="relative h-[59px] w-button-large overflow-hidden rounded">
              {/* Gray background */}
              <div className="absolute inset-0 bg-gray" />
              {/* Green progress fill */}
              <div
                className="duration-300 absolute inset-y-0 left-0 bg-green transition-all ease-linear"
                style={{ width: `${progress}%` }}
              />
              <ButtonBase
                className="relative z-10 flex h-full w-full items-center justify-center"
                onClick={() => onSaveAndNext()}
                disabled
              >
                <Text fontWeight="semibold" className="text-white">
                  {POST_INSPECTION_FLOW_STRINGS['pricing.getStarted.cta']}
                </Text>
              </ButtonBase>
            </div>
            {remainingTime && (
              <Text variant="p1" className="text-navy">
                {getString(
                  POST_INSPECTION_FLOW_STRINGS['pricing.getStarted.videoHint'],
                  { remainingTime },
                )}
              </Text>
            )}
          </>
        )}
      </div>

      {/* Priced On heading */}
      <div className="mb-xl mt-3xl md:mt-0">
        <Text
          fontWeight="semibold"
          variant="h3"
          className="text-center text-navy"
        >
          {getString(
            POST_INSPECTION_FLOW_STRINGS['pricing.getStarted.pricedOn'],
            {
              month: formatDateTime({
                dateTime: stepResource.report.publishedOn,
                format: DATE_FORMATS.MONTH,
              }),
              day: formatDateTime({
                dateTime: stepResource.report.publishedOn,
                format: DATE_FORMATS.DAY_OF_MONTH,
              }),
            },
          )}
        </Text>
      </div>

      {/* Employee profile */}
      <MultipleBelongEmployeeProfileView employees={[assignedEmployee]} />

      {/* Mobile fixed bottom CTA with progress */}
      <div className="my-2xl flex w-full flex-col md:hidden">
        {videoEnded ? (
          <ButtonBase
            className="rounded h-2xl w-full bg-green transition duration-hover hover:bg-light-green focus:bg-light-green active:bg-light-green"
            onClick={() => onSaveAndNext()}
          >
            <Text fontWeight="semibold" className="text-white">
              {POST_INSPECTION_FLOW_STRINGS['pricing.getStarted.cta']}
            </Text>
          </ButtonBase>
        ) : (
          <>
            {remainingTime && (
              <div
                className="flex justify-center pb-2xs pt-2xl rounded"
                style={{
                  background:
                    'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,1) 100%)',
                }}
              >
                <Text variant="p1" className="text-navy">
                  {getString(
                    POST_INSPECTION_FLOW_STRINGS[
                      'pricing.getStarted.videoHint'
                    ],
                    { remainingTime },
                  )}
                </Text>
              </div>
            )}
            <div className="relative h-2xl w-full rounded">
              {/* Gray background */}
              <div className="absolute inset-0 bg-gray rounded" />
              {/* Green progress fill */}
              <div
                className="duration-300 absolute inset-y-0 left-0 bg-green transition-all ease-linear rounded"
                style={{ width: `${progress}%` }}
              />
              <ButtonBase
                className="relative z-10 h-full w-full rounded"
                disabled
              >
                <Text fontWeight="semibold" className="text-white">
                  {POST_INSPECTION_FLOW_STRINGS['pricing.getStarted.cta']}
                </Text>
              </ButtonBase>
            </div>
          </>
        )}
      </div>
    </PostInspectionContentLayout>
  );
};

export default GetStarted;
